import { useEffect } from 'react';
import type { SelfServiceData, StepId } from '../../types';
import { selfServiceApi } from '../../services/selfServiceApi';
import { CURRENT_STATE_VERSION } from '../utils/constants';

interface UseWizardPersistenceParams {
  sessionId: string | null;
  isLoading: boolean;
  selfServiceData: SelfServiceData;
  currentStepId: StepId;
  history: StepId[];
  api?: typeof selfServiceApi;
}

export const useWizardPersistence = ({
  sessionId,
  isLoading,
  selfServiceData,
  currentStepId,
  history,
  api = selfServiceApi
}: UseWizardPersistenceParams): void => {
  useEffect(() => {
    if (!sessionId) {
      return;
    }

    try {
      const snapshot = JSON.stringify({
        selfServiceData,
        currentStepId,
        history
      });
      sessionStorage.setItem(`wizardState:${sessionId}`, snapshot);
    } catch (error) {
      console.warn('Failed to persist wizard state locally', error);
    }
  }, [sessionId, selfServiceData, currentStepId, history]);

  useEffect(() => {
    if (!sessionId || isLoading) {
      return;
    }

    const timer = setTimeout(() => {
      void api.saveState(sessionId, {
        selfServiceData,
        currentStepId,
        history,
        stateVersion: CURRENT_STATE_VERSION
      }).catch(error => {
        console.error('Failed to save state:', error);
      });
    }, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, [api, sessionId, isLoading, selfServiceData, currentStepId, history]);

  useEffect(() => {
    if (!sessionId || isLoading) {
      return;
    }

    const onHide = () => {
      void api.saveState(sessionId, {
        selfServiceData,
        currentStepId,
        history,
        stateVersion: CURRENT_STATE_VERSION
      }).catch(() => {
        // ignore best-effort errors
      });
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        onHide();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [api, sessionId, isLoading, selfServiceData, currentStepId, history]);
};
